/** Settings-area data hooks — users, teams, workspace (§7.2, §7.7). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Designation, SystemRole } from '@virtuetasks/types';
import { api, qs } from '@/lib/api';

export interface PersonView {
  _id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  avatarUrl?: string;
  phone?: string;
  phoneVerified: boolean;
  systemRole: SystemRole;
  designation: Designation;
  teamIds: string[];
  skills: string[];
  timezone: string;
  capacityHoursPerWeek: number;
  status: 'invited' | 'active' | 'suspended';
  lastActiveAt?: string;
  twoFactorEnabled: boolean;
  /** V7-01 §2.3 — browsers this person has push enabled on. Coverage, not content. */
  pushDevices?: number;
}

/** What became of a pending invitation — one entry per never-accepted person. */
export interface InvitationSummary {
  status: 'pending' | 'accepted' | 'expired' | 'revoked' | null;
  sentAt: string | null;
  expiresAt: string | null;
  expired: boolean;
  lastDelivery: {
    status: 'sent' | 'failed' | 'skipped';
    errorMessage: string | null;
    skipReason: string | null;
    at: string;
  } | null;
}

export interface TeamView {
  _id: string;
  name: string;
  description?: string;
  colour: string;
  leadId: string;
  memberIds: string[];
}

export const usersKey = (filters: Record<string, unknown> = {}) => ['users', filters] as const;
export const teamsKey = ['teams'] as const;
export const workspaceKey = ['workspace'] as const;

export function useUsers(filters: { q?: string; status?: string; role?: string; teamId?: string } = {}) {
  return useQuery({
    queryKey: usersKey(filters),
    queryFn: async () => {
      const res = await api.get<PersonView[]>(`/users${qs({ ...filters, limit: 200 })}`);
      return res.data;
    },
  });
}

export function useTeams() {
  return useQuery({
    queryKey: teamsKey,
    queryFn: async () => (await api.get<TeamView[]>('/teams')).data,
  });
}

export interface WorkspaceView {
  _id: string;
  name: string;
  ownerId: string;
  timezone: string;
  workingDays: number[];
  workingHours: { start: string; end: string };
  holidays: { date: string; name: string }[];
  settings: {
    allowedInviteDomains: string[];
    defaultProjectVisibility: 'workspace' | 'private';
    requireTwoFactorForAdmins: boolean;
    taskDeleteGraceMinutes: number;
  };
}

export function useWorkspace() {
  return useQuery({
    queryKey: workspaceKey,
    queryFn: async () => (await api.get<WorkspaceView>('/settings/workspace')).data,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      systemRole: SystemRole;
      designation: Designation;
      teamIds: string[];
      projectIds: string[];
    }) => (await api.post<{ resent: boolean }>('/users/invite', input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      // The new row's "sent … expires …" line comes from the summaries, not
      // from the user record — without this it renders bare until a reload.
      void qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, systemRole }: { userId: string; systemRole: SystemRole }) =>
      (await api.patch<PersonView>(`/users/${userId}/role`, { systemRole })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, patch }: { userId: string; patch: Record<string, unknown> }) =>
      (await api.patch<PersonView>(`/users/${userId}`, patch)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, active }: { userId: string; active: boolean }) => {
      const res = await api.post<PersonView | { user: PersonView; openTaskCount: number }>(
        `/users/${userId}/${active ? 'reactivate' : 'deactivate'}`,
      );
      // Deactivate answers with the open-task count wrapped around the person;
      // reactivate answers with the person alone. Normalise, so callers can read
      // the restored status without knowing which endpoint they hit.
      return 'user' in res.data ? res.data : { user: res.data, openTaskCount: 0 };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/**
 * Invitation state for the whole list, in one request.
 *
 * Keyed by user id rather than fetched per row: the Members table is mostly
 * accepted members, and a request per row would be an N+1 for a minority.
 */
export function useInvitationSummaries() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: async () => (await api.get<Record<string, InvitationSummary>>('/users/invitations')).data,
  });
}

/** Anything that changes an invitation invalidates both the list and the summaries. */
function useInvitationMutation<TArgs, TResult>(run: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useUpdateInviteEmail() {
  return useInvitationMutation(
    async ({ userId, email }: { userId: string; email: string }) =>
      (await api.patch<PersonView>(`/users/${userId}/invitation/email`, { email })).data,
  );
}

export function useResendInvite() {
  return useInvitationMutation(
    async (userId: string) =>
      (await api.post<InvitationSummary>(`/users/${userId}/invitation/resend`)).data,
  );
}

export function useInviteLink() {
  return useInvitationMutation(
    async (userId: string) =>
      (await api.post<{ url: string; expiresAt: string }>(`/users/${userId}/invitation/link`)).data,
  );
}

export function useCancelInvite() {
  return useInvitationMutation(
    async (userId: string) =>
      (await api.delete<{ deleted: true; email: string }>(`/users/${userId}/invitation`)).data,
  );
}

export function useChangeMemberEmail() {
  return useInvitationMutation(
    async ({ userId, email }: { userId: string; email: string }) =>
      (await api.patch<{ pendingEmail: string; expiresAt: string }>(`/users/${userId}/email`, { email }))
        .data,
  );
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      colour: string;
      leadId: string;
      memberIds: string[];
    }) => (await api.post<TeamView>('/teams', input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamsKey });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useTeamMembers() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: teamsKey });
    void qc.invalidateQueries({ queryKey: ['users'] });
  };
  return {
    add: useMutation({
      mutationFn: async ({ teamId, userIds }: { teamId: string; userIds: string[] }) =>
        (await api.post<TeamView>(`/teams/${teamId}/members`, { userIds })).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async ({ teamId, userIds }: { teamId: string; userIds: string[] }) =>
        (await api.delete<TeamView>(`/teams/${teamId}/members`, { userIds })).data,
      onSuccess: invalidate,
    }),
    destroy: useMutation({
      mutationFn: async (teamId: string) => api.delete(`/teams/${teamId}`),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Personal preferences — theme, density, landing view, notification matrix,
 * quiet hours (§F21).
 *
 * The matrix is always sent whole. Its keys contain dots (`task.assigned`), and
 * a dotted `$set` path would be read by Mongo as nested fields rather than as
 * one key — which is exactly how notification batching was silently broken once.
 */
export interface PreferencesPatch {
  theme?: 'light' | 'dark' | 'system';
  density?: 'comfortable' | 'compact';
  defaultView?: string;
  defaultProjectId?: string | null;
  quietHours?: { enabled: boolean; start: string; end: string };
  notificationMatrix?: Record<string, Record<string, boolean>>;
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PreferencesPatch) =>
      (await api.patch<PreferencesPatch>('/users/me/preferences', patch)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

/**
 * Whether WhatsApp exists at all in this installation — ruling 5.
 *
 * Off by default and hidden entirely when off: no column in the notification
 * matrix, no phone verification, no settings section. Dead UI for a channel
 * nobody has configured is worse than no UI, because it invites people to
 * configure something that will not work.
 */
export interface InstallationFeatures {
  whatsappEnabled: boolean;
  pushPublicKey: string | null;
}

/**
 * Installation-wide switches, readable by any signed-in user.
 *
 * Not the admin `/status` endpoint: every member's notification matrix depends
 * on whether WhatsApp exists, and a member cannot read an admin surface — so
 * asking there returned 403 and the column stayed hidden for the wrong reason.
 */
export function useFeatures() {
  return useQuery({
    queryKey: ['installation-features'],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () =>
      (await api.get<InstallationFeatures>('/settings/integrations/features')).data,
  });
}

export function useWhatsappEnabled(): boolean {
  return useFeatures().data?.whatsappEnabled === true;
}
